import { Injectable } from '@angular/core';
import { ToastInitConfig, ToastInsideConfig, ToastPositions, ToastResponse } from '../toast.config';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';

export interface ToastProgressEvent {
  count: number;
  progress: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToasService {
  count: number = 0;
  private aAllToast: { [key: string]: ToastInsideConfig } = {};
  private readonly ToastSubject$ = new BehaviorSubject<{ [key: string]: ToastInsideConfig }>({});  
  toastObs = this.ToastSubject$.asObservable();
  private responseToast: { [key: string]: ToastResponse } = {};
  private readonly toastResponseSubject$ = new BehaviorSubject<{ [key: string]: ToastResponse }>(this.responseToast);
  responseObs = this.toastResponseSubject$.asObservable();
  private readonly closeAllToast$ = new BehaviorSubject<boolean>(false);
  closeAllToastObs = this.closeAllToast$.asObservable();
  private readonly allToastClosed$ = new Subject<void>();
  readonly allToastClosedObs = this.allToastClosed$.asObservable();
  private readonly flushQueue$ = new Subject<void>();
  readonly flushQueueObs = this.flushQueue$.asObservable();
  private batchQueue: ToastInitConfig[] = [];
  private batchProcessing = false;

  private readonly progressSubject$ = new BehaviorSubject<ToastProgressEvent | null>(null);
  readonly progressObs = this.progressSubject$.asObservable();

  initBatch(toasts: ToastInitConfig[]): void {
    this.batchQueue = [...toasts]; 
    this.processBatch();
  }

  private processBatch(): void {
  if (this.batchQueue.length === 0) {
    this.batchProcessing = false;
    return;
  }
  this.batchProcessing = true;
  const toastData = this.batchQueue.shift()!;
  this.init(toastData);
  setTimeout(() => this.processBatch(), 800);
}

  updateToastData(toastData: ToastInsideConfig) {
    this.aAllToast[toastData.count!] = toastData;
    this.ToastSubject$.next({ ...this.aAllToast });
  }

  async deleteToast(toastData: ToastInsideConfig) {
    const response: ToastResponse = {
      isChecked: toastData.isChecked,
      confirmRedirection: toastData.confirmRedirection
    };
    this.responseToast[toastData.count!] = response;
    this.toastResponseSubject$.next({ ...this.responseToast });
    delete this.responseToast[toastData.count!];
    delete this.aAllToast[toastData.count!];
    this.toastResponseSubject$.next({ ...this.responseToast });
    this.ToastSubject$.next({ ...this.aAllToast });
  }

  async closeAllToast() {
    this.batchQueue = []; 
    this.batchProcessing = false;
    this.aAllToast = {};
    this.responseToast = {};
    this.count = 0;
    this.ToastSubject$.next({});
    this.toastResponseSubject$.next({});
    this.flushQueue$.next();
  }

  init(toastData: ToastInitConfig): Observable<ToastResponse> {
    let id = this.count;
    this.count++;
    toastData.position = toastData.position || 'top-r';
    this.aAllToast[id] = {
      ...toastData,
      bMostrar: true,
      count: id,
      positionCss: ToastPositions[toastData.position]
    };
    this.ToastSubject$.next({ ...this.aAllToast });
    return this.responseObs.pipe(
      map(resp => resp[id] || {})
    );
  }

  get hasActiveToasts(): boolean {
    return Object.keys(this.aAllToast).length > 0;
  }

  /** Crea un toast y retorna su count para poder controlarlo externamente */
  initWithCount(toastData: ToastInitConfig): { count: number; response$: Observable<ToastResponse> } {
    const id = this.count;
    this.count++;
    toastData.position = toastData.position || 'top-r';
    this.aAllToast[id] = {
      ...toastData,
      bMostrar: true,
      count: id,
      positionCss: ToastPositions[toastData.position]
    };
    this.ToastSubject$.next({ ...this.aAllToast });
    return {
      count: id,
      response$: this.responseObs.pipe(map(resp => resp[id] || {}))
    };
  }

  /** Cierra un toast específico por su count */
  closeToastByCount(count: number): void {
    if (this.aAllToast[count]) {
      this.aAllToast[count].bMostrar = false;
      this.ToastSubject$.next({ ...this.aAllToast });
    }
  }

  /** Actualiza el progreso manual de un toast existente */
  updateProgress(count: number, progress: number): void {
    if (this.aAllToast[count]) {
      this.aAllToast[count].progress = Math.max(0, Math.min(100, progress));
      this.ToastSubject$.next({ ...this.aAllToast });
    }
    this.progressSubject$.next({ count, progress: Math.max(0, Math.min(100, progress)) });
  }
}